import React from 'react'
import { useTranslation } from 'react-i18next'
import { WEDDING_CAMERA_PRICES } from '../../constants/pricing.ts'
import { useParams } from 'react-router-dom'
import useSeo, { buildAlternates, SITE_URL } from '../../hooks/useSeo.ts'
import { SupportedLanguage } from '../../types/i18n.ts'
import { normalizeLanguage, withLang } from '../../utils/langPath.ts'

type MarketingPage = 'home' | 'price' | 'demo' | 'planner'

interface SeoCopy {
	title: string
	description: string
}

const COPY: Record<SupportedLanguage, Record<MarketingPage, SeoCopy>> = {
	hr: {
		home: {
			title: 'QR album za vjenčanje i slike gostiju | Wedding Camera',
			description: 'Prikupite slike sa svadbe u zajednički online album. QR kod, aplikacija za iPhone i Android te PIN za goste. Pogledajte kako radi QR galerija i odaberite paket.'
		},
		price: {
			title: 'QR album za vjenčanje: cijene i paketi | Wedding Camera',
			description: 'Usporedite pakete za QR album: broj gostiju, fotografije, video i online galerija. Svi paketi uključuju dizajn letka s QR kodom i PIN-om za vaše vjenčanje.'
		},
		demo: {
			title: 'Isprobajte Wedding Camera | Demo vjenčanje',
			description: 'Isprobajte kako gosti skeniraju QR kod, unose PIN i dijele fotografije u zajedničkoj galeriji za vjenčanje.'
		},
		planner: {
			title: 'Wedding Planner | Jednostavan raspored sjedenja za vjenčanje',
			description: 'Organizirajte listu gostiju i raspored sjedenja bez Excel tablica. Isprobajte besplatni Wedding Planner za svoje vjenčanje.'
		}
	},
	sr: {
		home: {
			title: 'QR album za venčanje i slike sa svadbe | Wedding Camera',
			description: 'Prikupite slike sa svadbe u zajednički onlajn album. QR kod, aplikacija za iPhone i Android i PIN za goste. Pogledajte demo i pakete za vaše venčanje.'
		},
		price: {
			title: 'QR album za venčanje: cene i paketi | Wedding Camera',
			description: 'Uporedite pakete za QR album: broj gostiju, fotografije, video i onlajn galerija. Svi paketi uključuju dizajn letka sa QR kodom i PIN-om.'
		},
		demo: {
			title: 'Isprobajte Wedding Camera | Demo venčanje',
			description: 'Isprobajte kako gosti skeniraju QR kod, unose PIN i dele fotografije u zajedničkoj galeriji za venčanje.'
		},
		planner: {
			title: 'Wedding Planner | Jednostavan raspored sedenja za venčanje',
			description: 'Organizujte listu gostiju i raspored sedenja bez Excel tabela. Isprobajte besplatni Wedding Planner za svoje venčanje.'
		}
	},
	bs: {
		home: {
			title: 'QR album za vjenčanje i slike sa svadbe | Wedding Camera',
			description: 'Prikupite slike sa svadbe u zajednički online album. QR kod, aplikacija za iPhone i Android i PIN za goste. Pogledajte demo i pakete za vaše vjenčanje.'
		},
		price: {
			title: 'QR album za vjenčanje: cijene i paketi | Wedding Camera',
			description: 'Uporedite pakete za QR album: broj gostiju, fotografije, video i online galerija. Svi paketi uključuju dizajn letka s QR kodom i PIN-om.'
		},
		demo: {
			title: 'Isprobajte Wedding Camera | Demo vjenčanje',
			description: 'Isprobajte kako gosti skeniraju QR kod, unose PIN i dijele fotografije u zajedničkoj galeriji za vjenčanje.'
		},
		planner: {
			title: 'Wedding Planner | Jednostavan raspored sjedenja za vjenčanje',
			description: 'Organizirajte listu gostiju i raspored sjedenja bez Excel tablica. Isprobajte besplatni Wedding Planner za svoje vjenčanje.'
		}
	},
	sl: {
		home: {
			title: 'Poročni album s QR-kodo in fotografije gostov | Wedding Camera',
			description: 'Zberite fotografije gostov v skupnem spletnem poročnem albumu. QR-koda, aplikacija za iPhone in Android ter PIN. Oglejte si demo in poročne pakete.'
		},
		price: {
			title: 'Poročni album s QR-kodo: cene in paketi | Wedding Camera',
			description: 'Primerjajte pakete za poročni album: gostje, fotografije, video in spletna galerija. Vsi vključujejo oblikovan letak s QR-kodo in PIN-om.'
		},
		demo: {
			title: 'Preizkusite Wedding Camera | Demo poroka',
			description: 'Preizkusite, kako gostje skenirajo QR-kodo, vnesejo PIN in delijo fotografije v skupni poročni galeriji.'
		},
		planner: {
			title: 'Wedding Planner | Preprost razpored sedenja za poroko',
			description: 'Organizirajte seznam gostov in razpored sedenja brez Excelovih tabel. Preizkusite brezplačni Wedding Planner.'
		}
	},
	de: {
		home: {
			title: 'Hochzeitsfotos per QR-Code sammeln | Wedding Camera',
			description: 'Sammelt Hochzeitsfotos eurer Gäste in einem gemeinsamen Online-Album. Mit QR-Code, PIN und App für iPhone und Android. Entdeckt Demo und Pakete.'
		},
		price: {
			title: 'Hochzeitsalbum mit QR-Code: Preise | Wedding Camera',
			description: 'Vergleicht Pakete für eure Gästefotos: Gästezahl, Fotos, Videos und Online-Galerie. Alle Pakete enthalten einen druckfertigen Flyer mit QR-Code und PIN.'
		},
		demo: {
			title: 'Wedding Camera testen | Demo-Hochzeit',
			description: 'Erlebt, wie Gäste den QR-Code scannen, den PIN eingeben und Fotos in einer gemeinsamen Hochzeitsgalerie teilen.'
		},
		planner: {
			title: 'Wedding Planner | Sitzordnung für eure Hochzeit planen',
			description: 'Organisiert Gästeliste und Sitzordnung ohne Excel-Tabellen. Testet den kostenlosen Wedding Planner für eure Hochzeit.'
		}
	},
	en: {
		home: {
			title: 'QR Code Wedding Album for Guest Photos | Wedding Camera',
			description: 'Collect wedding guest photos in one shared online album. QR code, PIN and an iPhone and Android app. Explore the demo and wedding photo packages.'
		},
		price: {
			title: 'QR Code Wedding Album Pricing | Wedding Camera',
			description: 'Compare wedding photo packages: guest numbers, photos, video and an online gallery. Every package includes a print-ready leaflet with a QR code and PIN.'
		},
		demo: {
			title: 'Try Wedding Camera | Demo wedding gallery',
			description: 'See how guests scan a QR code, enter a PIN and share photos in one private wedding gallery.'
		},
		planner: {
			title: 'Wedding Planner | Simple wedding seating plan',
			description: 'Organize your guest list and seating plan without spreadsheets. Try Wedding Planner free for your wedding.'
		}
	}
}

const PATHS: Record<MarketingPage, string> = {
	home: '/',
	price: '/price',
	demo: '/demo',
	planner: '/weddingPlanner'
}

const SOCIAL_IMAGE = 'https://firebasestorage.googleapis.com/v0/b/wedding-94824.appspot.com/o/images%2Fwedding-camera.png?alt=media&token=253b8e7a-3866-4ebd-a8f5-0e85b23ed677'

interface Props {
	page: MarketingPage
}

const MarketingSeo: React.FC<Props> = ({ page }) => {
	const { t } = useTranslation('home')
	const { lang: langParam } = useParams<{ lang?: string }>()
	const lang = normalizeLanguage(langParam)
	const copy = COPY[lang][page]
	const path = withLang(lang, PATHS[page])

	useSeo({
		title: copy.title,
		description: copy.description,
		path,
		lang,
		image: SOCIAL_IMAGE,
		alternates: buildAlternates(code => withLang(code, PATHS[page])),
		jsonLd: [
			{
				'@context': 'https://schema.org',
				'@type': 'WebPage',
				name: copy.title,
				description: copy.description,
				url: `${SITE_URL}${path}`,
				inLanguage: lang,
				isPartOf: { '@id': `${SITE_URL}/#website` },
				about: { '@id': `${SITE_URL}/#application` }
			},
			...(page === 'home'
				? [{
					'@context': 'https://schema.org',
					'@type': 'MobileApplication',
					'@id': `${SITE_URL}/#application`,
					description: t('qrAlbum.text'),
					publisher: { '@id': `${SITE_URL}/#organization` },
					installUrl: [
						'https://apps.apple.com/us/app/id6608982445',
						'https://play.google.com/store/apps/details?id=com.mcode.weddingcam'
					],
					name: 'Wedding Camera',
					applicationCategory: 'PhotographyApplication',
					operatingSystem: 'iOS, Android',
					url: `${SITE_URL}${path}`,
					offers: Object.entries(WEDDING_CAMERA_PRICES).map(([name, price]) => ({
						'@type': 'Offer',
						name,
						price: price.currentPrice.replace('€', '').replace(',', '.'),
						priceCurrency: 'EUR',
						url: `${SITE_URL}${withLang(lang, '/price')}`
					}))
				}, {
					'@context': 'https://schema.org',
					'@type': 'Organization',
					'@id': `${SITE_URL}/#organization`,
					name: 'Wedding Camera',
					url: `${SITE_URL}/`,
					logo: `${SITE_URL}/logo512.png`
				}, {
					'@context': 'https://schema.org',
					'@type': 'WebSite',
					'@id': `${SITE_URL}/#website`,
					name: 'Wedding Camera',
					url: `${SITE_URL}/`,
					publisher: { '@id': `${SITE_URL}/#organization` }
				}]
				: [])
		]
	})

	return null
}

export default MarketingSeo
